import { useSyncExternalStore } from "react";

import { parseCompanyLogoUrl } from "../../onboardingStore";
import {
  normalizeMorrowDicebearAvatarSeed,
  randomMorrowDicebearAvatarSeed,
} from "./morrowPersonAvatarCatalog";

export const MORROW_USER_PROFILE_STORAGE_KEY = "morrow:user-profile:v1";
export const MORROW_USER_TITLE_META = "morrowTitle";
export const MORROW_USER_ROLE_META = "morrowRole";
export const MORROW_USER_AVATAR_META = "morrowAvatarSeed";
/** Legacy Clerk metadata key from the first avatar rollout. */
const LEGACY_MORROW_USER_AVATAR_META = "morrowAvatarCharacter";
/** Clerk unsafeMetadata key mirroring the onboarding questionnaire's industry. */
export const MORROW_USER_INDUSTRY_META = "morrowIndustry";
export const LOCAL_SELF_MEMBER_ID = "local-self";

export type MorrowUserProfile = {
  readonly title: string;
  readonly role: string;
  readonly avatarSeed: string;
  /** User-uploaded portrait stored locally (data URL or https). */
  readonly portraitImageUrl: string;
};

const EMPTY_PROFILE: MorrowUserProfile = {
  title: "",
  role: "",
  avatarSeed: "",
  portraitImageUrl: "",
};

const listeners = new Set<() => void>();
let cachedProfile: MorrowUserProfile | null = null;

function safeStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readAvatarSeed(value: unknown): string {
  if (typeof value !== "string") return "";
  return normalizeMorrowDicebearAvatarSeed(value) ?? "";
}

export function parseUserProfile(value: unknown): MorrowUserProfile {
  if (!isRecord(value)) return EMPTY_PROFILE;
  return {
    title: typeof value.title === "string" ? value.title.trim() : "",
    role: typeof value.role === "string" ? value.role.trim() : "",
    avatarSeed: readAvatarSeed(value.avatarSeed ?? value.avatarCharacterId),
    portraitImageUrl: parseCompanyLogoUrl(value.portraitImageUrl),
  };
}

export function userProfileFromMetadata(value: unknown): MorrowUserProfile {
  if (!isRecord(value)) return EMPTY_PROFILE;
  const title = value[MORROW_USER_TITLE_META];
  const role = value[MORROW_USER_ROLE_META];
  const avatarSeed = value[MORROW_USER_AVATAR_META] ?? value[LEGACY_MORROW_USER_AVATAR_META];
  return {
    title: typeof title === "string" ? title.trim() : "",
    role: typeof role === "string" ? role.trim() : "",
    avatarSeed: readAvatarSeed(avatarSeed),
    portraitImageUrl: "",
  };
}

export function metadataFromUserProfile(
  current: Record<string, unknown>,
  profile: MorrowUserProfile,
): Record<string, unknown> {
  const next: Record<string, unknown> = {
    ...current,
    [MORROW_USER_TITLE_META]: profile.title,
    [MORROW_USER_ROLE_META]: profile.role,
    [MORROW_USER_AVATAR_META]: profile.avatarSeed,
  };
  delete next[LEGACY_MORROW_USER_AVATAR_META];
  return next;
}

function readProfile(): MorrowUserProfile {
  const storage = safeStorage();
  if (!storage) return EMPTY_PROFILE;
  try {
    const raw = storage.getItem(MORROW_USER_PROFILE_STORAGE_KEY);
    if (!raw) return EMPTY_PROFILE;
    return parseUserProfile(JSON.parse(raw));
  } catch {
    return EMPTY_PROFILE;
  }
}

function emit() {
  for (const listener of listeners) listener();
}

/** Assign a random DiceBear seed the first time a profile is read without one. */
export function ensureUserAvatarSeed(): MorrowUserProfile {
  const current = cachedProfile ?? readProfile();
  if (current.avatarSeed) return current;
  return writeUserProfile({ avatarSeed: randomMorrowDicebearAvatarSeed() });
}

export function getUserProfile(): MorrowUserProfile {
  return (cachedProfile ??= readProfile());
}

export function subscribeToUserProfile(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function writeUserProfile(patch: Partial<MorrowUserProfile>): MorrowUserProfile {
  const current = getUserProfile();
  const next: MorrowUserProfile = {
    title: patch.title !== undefined ? patch.title.trim() : current.title,
    role: patch.role !== undefined ? patch.role.trim() : current.role,
    avatarSeed:
      patch.avatarSeed !== undefined
        ? (normalizeMorrowDicebearAvatarSeed(patch.avatarSeed) ?? "")
        : current.avatarSeed,
    portraitImageUrl:
      patch.portraitImageUrl !== undefined
        ? parseCompanyLogoUrl(patch.portraitImageUrl)
        : current.portraitImageUrl,
  };
  const storage = safeStorage();
  if (storage) {
    try {
      storage.setItem(MORROW_USER_PROFILE_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Best-effort; the in-memory snapshot still updates for this session.
    }
  }
  cachedProfile = next;
  emit();
  return next;
}

export function resetUserProfile(): void {
  const storage = safeStorage();
  if (storage) {
    try {
      storage.removeItem(MORROW_USER_PROFILE_STORAGE_KEY);
    } catch {
      // Best-effort.
    }
  }
  cachedProfile = EMPTY_PROFILE;
  emit();
}

export function useUserProfile(): MorrowUserProfile {
  return useSyncExternalStore(subscribeToUserProfile, getUserProfile, getUserProfile);
}
